import { toast } from 'react-toastify';
import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CreditAnalysis } from './types';
import { ColumnDef } from './components/DataTable.types';
import ApprovalModal from './ApprovalModal';
import BatchApprovalModal from './BatchApprovalModal';
import UnifiedDraweeModal from './UnifiedDraweeModal';
import PageHeader from '../../molecules/PageHeader';
import appConfig from '../../../config/app-config';
import DataTable from './components/DataTable';
import { saveDecision, saveBatchDecision, getFeatureFlags, getBatchDetail, BatchDetail } from '../../../services/CreditAnalysisService';
import { buildAnalysisColumns } from './shared/analysisColumns';
import { exportAnalysisToExcel } from './shared/exportToExcel';
import { parseRangeFilter } from './shared/parseRangeFilter';

const { address: CAE_API_URL, key: CAE_API_KEY } = appConfig.apis.creditAnalysisApiEnv;

export default function BatchAnalysisView() {
    const { batchId } = useParams<{ batchId: string }>();

    const [batch, setBatch] = useState<BatchDetail | null>(null);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [showFilters, setShowFilters] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState<CreditAnalysis | null>(null);
    const [analysisData, setAnalysisData] = useState<CreditAnalysis[]>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [isBatchApprovalModalOpen, setIsBatchApprovalModalOpen] = useState(false);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [draweeModalName, setDraweeModalName] = useState<string | null>(null);
    const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

    useEffect(() => { getFeatureFlags().then(setFeatureFlags).catch(() => {}); }, []);

    useEffect(() => {
        if (!batchId) return;
        getBatchDetail(batchId).then(setBatch).catch(err => console.error('Failed to load batch:', err));
    }, [batchId]);

    useEffect(() => {
        if (!batchId) return;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (CAE_API_KEY) headers['x-api-key'] = CAE_API_KEY;

        const params = new URLSearchParams();
        params.set('batchId', batchId);
        params.set('size', '9999');

        fetch(`${CAE_API_URL}/analysis-items?${params.toString()}`, { headers })
            .then(res => res.json())
            .then(data => { setAnalysisData(data.content || []); })
            .catch(() => { toast.error('Erro ao carregar dados'); setAnalysisData([]); });
    }, [batchId]);

    const { filteredData } = useMemo(() => {
        let filtered = [...analysisData];
        const multiselectFields = new Set(['rating', 'recommendation', 'status', 'type']);
        const rangeFields = new Set(['originalValue', 'approvedValue', 'suggestedValue', 'currentExposure', 'potentialExposure', 'scrTotal', 'scrRural', 'overdue', 'qsa', 'annualRevenue', 'equity', 'score', 'pdef', 'scoreQuod', 'ccirHa', 'ccirValue', 'patrimonioScr']);

        Object.entries(filters).forEach(([field, value]) => {
            if (!value) return;
            if (rangeFields.has(field)) {
                const predicate = parseRangeFilter(value);
                if (!predicate) return;
                filtered = filtered.filter(item => {
                    const raw = (item as any)[field];
                    const num = parseFloat(String(raw || '0').replace(/[^\d.,-]/g, '').replace(',', '.'));
                    return !isNaN(num) && predicate(num);
                });
                return;
            }
            const vals = value.split(';').map(v => v.trim().toLowerCase()).filter(Boolean);
            const isMultiselect = multiselectFields.has(field);
            filtered = filtered.filter(item => {
                const iv = (item as any)[field];
                if (!iv) return isMultiselect && vals.includes('-');
                const low = String(iv).toLowerCase();
                return isMultiselect ? vals.some(v => v === low) : vals.some(v => low.includes(v));
            });
        });

        if (sortField) {
            filtered.sort((a, b) => {
                const cmp = String((a as any)[sortField] || '').localeCompare(String((b as any)[sortField] || ''), 'pt-BR', { numeric: true });
                return sortOrder === 'ASC' ? cmp : -cmp;
            });
        }

        return { filteredData: filtered };
    }, [filters, sortField, sortOrder, analysisData]);

    const handleSort = (field: string) => {
        if (sortField === field) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        else { setSortField(field); setSortOrder('ASC'); }
    };

    const handleSelectRow = (uuid: string) => {
        setSelectedRows(prev => { const s = new Set(prev); s.has(uuid) ? s.delete(uuid) : s.add(uuid); return s; });
    };

    const handleSelectAll = () => {
        setSelectedRows(prev => prev.size === filteredData.length ? new Set() : new Set(filteredData.map(r => r.uuid)));
    };

    const statusToApi: Record<string, string> = {
        'Pendente': 'PENDING', 'Aprovado': 'APPROVED', 'Reprovado': 'REJECTED', 'Pré-aprovado': 'PRE_APPROVED', 'Cancelado': 'CANCELED',
    };

    const handleSaveBatchApproval = async (decision: string, justification: string, observation: string) => {
        const selected = analysisData.filter(i => selectedRows.has(i.uuid));
        if (!selected.length) return;
        try {
            const apiStatus = statusToApi[decision] || decision;
            const isApproval = ['APPROVED', 'PRE_APPROVED'].includes(apiStatus);
            const byBatch: Record<string, typeof selected> = {};
            selected.forEach(i => { (byBatch[i.id] = byBatch[i.id] || []).push(i); });
            for (const bid of Object.keys(byBatch)) {
                const items = byBatch[bid].map((i: CreditAnalysis) => ({ itemId: i.uuid, approvedValue: isApproval ? i.originalValue : undefined }));
                await saveBatchDecision(bid, items, { status: apiStatus, justification: justification || undefined, creditObs: observation || undefined });
            }
            setAnalysisData(prev => prev.map(i => {
                if (!selectedRows.has(i.uuid)) return i;
                return { ...i, status: apiStatus, approvedValue: isApproval ? i.originalValue : '', justification: justification || i.justification, creditObs: observation || i.creditObs };
            }));
            setSelectedRows(new Set());
        } catch { toast.error('Erro ao salvar decisão em lote'); }
    };

    const columns = useMemo<ColumnDef<CreditAnalysis>[]>(() => buildAnalysisColumns({
        onOpenModal: (row) => { setSelectedAnalysis(row); setIsModalOpen(true); },
        onOpenDrawee: (doc, itemId, name, _batchId) => { setDraweeModalDoc(doc); setDraweeModalItemId(itemId); setDraweeModalName(name); },
        onSaveDecision: saveDecision,
        onUpdateRow: (uuid, updates) => setAnalysisData(prev => prev.map(i => i.uuid === uuid ? { ...i, ...updates } as CreditAnalysis : i)),
        featureFlags,
        hideBatchColumn: true,
    }), [featureFlags]);

    // ── Footer totals ────────────────────────────────────────────────
    const parseVal = (v: string) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const fmtPct = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
    const calcPdefPond = (items: CreditAnalysis[]) => {
        const sumW = items.reduce((s, r) => s + parseVal(r.approvedValue) * parseVal(r.pdef), 0);
        const sumA = items.reduce((s, r) => s + parseVal(r.approvedValue), 0);
        return sumA > 0 ? sumW / sumA : 0;
    };

    return (
        <div className="h-screen overflow-hidden flex flex-col bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Batch header */}
            <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-slate-600">
                <PageHeader
                    title={batch?.batchName || 'Carregando...'}
                    apiVersion="Credit Analysis - 1.0.0"
                    breadcrumb={[
                        { label: 'Processamentos', url: '/credit-analysis/processamentos' },
                        { label: 'Detalhes do Lote', url: `/credit-analysis/batch/${batchId}` },
                        { label: 'Análise', url: '#' },
                    ]}
                >
                    <div className="flex items-center gap-3 pt-6">
                        {batch && (
                            <span className={`px-3 py-1 rounded text-xs font-medium ${
                                batch.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                batch.status === 'ERROR' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>
                                {batch.status}
                            </span>
                        )}
                    </div>
                </PageHeader>
                {batch && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Número: {batch.batchNumber} | Cedente: {batch.assignorName} | Criado em: {new Date(batch.createdAt).toLocaleString('pt-BR')}
                    </p>
                )}
            </div>

            {/* Table area */}
            <div className="flex-1 overflow-hidden flex flex-col px-4">
                {/* Action bar */}
                <div className="mt-4 mb-2 flex items-center justify-end gap-3 p-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ações em Lote:</span>
                    <button onClick={() => setShowFilters(f => !f)} className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                        {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                    </button>
                    <button onClick={() => setFilters({})} className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-sm font-medium">
                        Limpar Filtros
                    </button>
                    <button onClick={() => exportAnalysisToExcel(filteredData)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                        Exportar Excel
                    </button>
                    <button
                        onClick={() => setIsBatchApprovalModalOpen(true)}
                        disabled={selectedRows.size === 0}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ display: featureFlags['credit-analysis:batch-approve'] === false ? 'none' : undefined }}
                    >
                        Aprovação em Lote
                    </button>
                </div>

                <DataTable<CreditAnalysis>
                    columns={columns}
                    data={filteredData}
                    rowKey={(row) => row.uuid}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    showFilters={showFilters}
                    showCheckbox={true}
                    selectedRows={selectedRows}
                    onSelectRow={handleSelectRow}
                    onSelectAll={handleSelectAll}
                    emptyMessage="Nenhum registro encontrado..."
                />

                {/* Footer with totals */}
                <div className="sticky bottom-0 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0 z-10 px-4 py-3">
                    {(() => {
                        const allOriginal = analysisData.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const allApproved = analysisData.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        const filtOriginal = filteredData.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const filtApproved = filteredData.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        const selItems = filteredData.filter(r => selectedRows.has(r.uuid));
                        const selOriginal = selItems.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const selApproved = selItems.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        return (
                            <div className="flex gap-12 text-sm">
                                <div>
                                    <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Lote ({analysisData.length} itens)</div>
                                    <div>Total duplicatas: <span className="font-semibold">{fmt(allOriginal)}</span></div>
                                    <div>Total aprovado: <span className="font-semibold">{fmt(allApproved)}</span></div>
                                    <div>Diferença: <span className="font-semibold">{fmt(allOriginal - allApproved)}</span></div>
                                    <div>PDefault Ponderado: <span className="font-semibold">{fmtPct(calcPdefPond(analysisData))}</span></div>
                                </div>
                                {filteredData.length < analysisData.length && (
                                    <div>
                                        <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Filtrado ({filteredData.length} itens)</div>
                                        <div>Total duplicatas: <span className="font-semibold">{fmt(filtOriginal)}</span></div>
                                        <div>Total aprovado: <span className="font-semibold">{fmt(filtApproved)}</span></div>
                                        <div>Diferença: <span className="font-semibold">{fmt(filtOriginal - filtApproved)}</span></div>
                                        <div>PDefault Ponderado: <span className="font-semibold">{fmtPct(calcPdefPond(filteredData))}</span></div>
                                    </div>
                                )}
                                {selItems.length > 0 && (
                                    <div>
                                        <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Selecionado ({selItems.length} itens)</div>
                                        <div>Total duplicatas: <span className="font-semibold">{fmt(selOriginal)}</span></div>
                                        <div>Total aprovado: <span className="font-semibold">{fmt(selApproved)}</span></div>
                                        <div>Diferença: <span className="font-semibold">{fmt(selOriginal - selApproved)}</span></div>
                                        <div>PDefault Ponderado: <span className="font-semibold">{fmtPct(calcPdefPond(selItems))}</span></div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Modals */}
            {selectedAnalysis && <ApprovalModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} analysis={selectedAnalysis} />}
            {draweeModalDoc && (
                <UnifiedDraweeModal
                    batchId={batchId || ''}
                    itemId={draweeModalItemId || ''}
                    document={draweeModalDoc}
                    draweeName={draweeModalName || undefined}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); setDraweeModalName(null); }}
                />
            )}
            <BatchApprovalModal isOpen={isBatchApprovalModalOpen} onClose={() => setIsBatchApprovalModalOpen(false)} selectedCount={selectedRows.size} onSave={handleSaveBatchApproval} />
        </div>
    );
}
